import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Youtube, Play } from "lucide-react";
import { youtubeFeed } from "../../lib/api";

export const useYoutube = () => useQuery({ queryKey: ["youtube"], queryFn: youtubeFeed, staleTime: 30 * 60_000, retry: false });

// Small "Watch on YouTube" link used in the footer and on the Press page.
export const YoutubeLink = ({ testid = "youtube-link", className = "" }) => {
  const { data } = useYoutube();
  if (!data?.url) return null;
  return <a href={data.url} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 hover:text-foreground ${className}`} data-testid={testid}><Youtube className="h-4 w-4" /> YouTube</a>;
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "");

// Thumbnail until clicked, then the video plays right here (privacy-enhanced embed).
const Player = ({ v, testid }) => {
  const [on, setOn] = useState(false);
  return (
    <div className="relative aspect-video bg-black" data-testid={testid}>
      {on ? (
        <iframe src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1`} title={v.title || "HearthVault video"} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen className="absolute inset-0 w-full h-full" data-testid={`${testid}-iframe`} />
      ) : (
        <button type="button" onClick={() => setOn(true)} className="group absolute inset-0 w-full h-full" aria-label={`Play ${v.title || "video"}`} data-testid={`${testid}-play`}>
          <img src={v.thumb} alt="" loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
          <span className="absolute inset-0 grid place-items-center"><span className="h-14 w-14 rounded-full bg-background/90 grid place-items-center group-hover:scale-110 transition-transform"><Play className="h-6 w-6 text-primary ml-0.5" /></span></span>
        </button>
      )}
    </div>
  );
};

// Home page: a pinned featured video plus the channel's newest uploads, all playable in place.
export const LatestVideos = () => {
  const { data } = useYoutube();
  const featured = data?.featured;
  const vids = (data?.videos || []).filter((v) => v.id !== featured?.id).slice(0, 3);
  if (!featured && !vids.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="latest-videos">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">On YouTube</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">Watch how families use HearthVault</h2>
        </div>
        <a href={data.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid="latest-videos-channel"><Youtube className="h-4 w-4" /> See the channel</a>
      </div>
      {featured && (
        <div className="mt-8 rounded-2xl overflow-hidden border border-border bg-card" data-testid="featured-video">
          <Player v={featured} testid="featured-video-player" />
          {featured.title && <div className="p-4 font-medium" data-testid="featured-video-title">{featured.title}</div>}
        </div>
      )}
      {vids.length > 0 && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {vids.map((v) => (
            <div key={v.id} className="rounded-2xl overflow-hidden border border-border bg-card" data-testid={`latest-video-${v.id}`}>
              <Player v={v} testid={`latest-video-player-${v.id}`} />
              <div className="p-4"><div className="font-medium leading-snug line-clamp-2">{v.title}</div><div className="text-xs text-muted-foreground mt-1">{fmt(v.published)}</div></div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
